import React, { useState, useEffect } from 'react';
import {
  Button,
  TextField,
  Grid,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import BaseDialog from './BaseDialog';
import GoogleMapsAutocomplete from './GoogleMapsAutocomplete';
import { customerService, Customer, CreateCustomerDto, CustomerSource, CreateCustomerWithJobDto } from '../../services/customerService';
import { businessService, Business } from '../../services/businessService';
import { BusinessUser } from '../../types/business';
import { sortUsers } from '../../utils/userHelpers';
import { extractApiErrorMessage } from '../../utils/apiUtils';
import { Job, JobType, Priority } from '../../services/jobService';

interface CustomerFormDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: (customer: Customer, initialJob?: Job) => void;
  business: Business | null;
  title?: string;
  defaultAssignedSalesPersonId?: string;
  createInitialJob?: boolean;
}

const CustomerFormDialog: React.FC<CustomerFormDialogProps> = ({
  open,
  onClose,
  onSuccess,
  business,
  title = 'Create Customer',
  defaultAssignedSalesPersonId,
  createInitialJob = false
}) => {
  const [formData, setFormData] = useState<CreateCustomerDto>({
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    address: '',
    city: '',
    country: '',
    source: CustomerSource.Other,
    assignedSalesPersonId: undefined
  });

  const [businessUsers, setBusinessUsers] = useState<BusinessUser[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [addressKey, setAddressKey] = useState(0);

  // Update address key when business data changes to force re-render
  useEffect(() => {
    if (business) {
      setAddressKey(prev => prev + 1);
    }
  }, [business?.defaultCountry, business?.defaultCity]);

  // Load business users when dialog opens
  useEffect(() => {
    if (open && business?.id) {
      loadBusinessUsers();
    }
  }, [open, business?.id]);

  // Update assigned salesperson when default changes
  useEffect(() => {
    if (defaultAssignedSalesPersonId) {
      setFormData(prev => ({
        ...prev,
        assignedSalesPersonId: defaultAssignedSalesPersonId
      }));
    }
  }, [defaultAssignedSalesPersonId]);

  // Validation functions
  const validateFirstName = (value: string): string | null => {
    if (!value.trim()) {
      return 'First name is required';
    }
    if (value.length > 100) {
      return 'First name cannot be longer than 100 characters';
    }
    return null;
  };

  const validateLastName = (value: string): string | null => {
    if (value && value.length > 100) {
      return 'Last name cannot be longer than 100 characters';
    }
    return null;
  };

  const validateEmail = (value: string): string | null => {
    if (value && value.trim()) {
      if (!/\S+@\S+\.\S+/.test(value)) {
        return 'Email is invalid';
      }
      if (value.length > 256) {
        return 'Email cannot be longer than 256 characters';
      }
    }
    return null;
  };

  const validatePhoneNumber = (value: string): string | null => {
    if (value && value.length > 20) {
      return 'Phone number cannot be longer than 20 characters';
    }
    return null;
  };

  const validateSource = (value: CustomerSource): string | null => {
    if (!value) {
      return 'Source is required';
    }
    return null;
  };

  const validateField = (field: string, value: string | CustomerSource): string | null => {
    switch (field) {
      case 'firstName':
        return validateFirstName(value as string);
      case 'lastName':
        return validateLastName(value as string);
      case 'email':
        return validateEmail(value as string);
      case 'phoneNumber':
        return validatePhoneNumber(value as string);
      case 'source':
        return validateSource(value as CustomerSource);
      default:
        return null;
    }
  };

  const handleFieldBlur = (field: string) => {
    const value = formData[field as keyof CreateCustomerDto] || '';
    const error = validateField(field, value);
    setValidationErrors(prev => ({
      ...prev,
      [field]: error || ''
    }));
  };

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};
    
    const firstNameError = validateFirstName(formData.firstName);
    if (firstNameError) errors.firstName = firstNameError;
    
    const lastNameError = validateLastName(formData.lastName || '');
    if (lastNameError) errors.lastName = lastNameError;
    
    const emailError = validateEmail(formData.email || '');
    if (emailError) errors.email = emailError;
    
    const phoneError = validatePhoneNumber(formData.phoneNumber || '');
    if (phoneError) errors.phoneNumber = phoneError;
    
    const sourceError = validateSource(formData.source);
    if (sourceError) errors.source = sourceError;
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleAddressChange = (address: string, latitude?: number, longitude?: number) => {
    setFormData(prev => ({
      ...prev,
      address
    }));
  };

  const loadBusinessUsers = async () => {
    if (!business?.id) return;

    try {
      setLoadingUsers(true);
      const users = await businessService.getBusinessUsers(business.id);
      setBusinessUsers(sortUsers(users));
    } catch (err: any) {
      console.error('Failed to load business users:', err);
      // Don't set error state for this - it's not critical for form submission
    } finally {
      setLoadingUsers(false);
    }
  };

  const resetForm = () => {
    setFormData({
      firstName: '',
      lastName: '',
      email: '',
      phoneNumber: '',
      address: '',
      city: '',
      country: '',
      secondaryContactName: '',
      secondaryContactPhone: '',
      secondaryContactEmail: '',
      source: CustomerSource.Other,
      assignedSalesPersonId: defaultAssignedSalesPersonId || undefined
    });
    setValidationErrors({});
    setError(null);
    setSuccess(null);
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      // Clean up form data - convert empty strings to undefined for optional fields
      const cleanedFormData = {
        ...formData,
        lastName: formData.lastName || undefined,
        email: formData.email || undefined,
        phoneNumber: formData.phoneNumber || undefined,
        address: formData.address || undefined,
        city: formData.city || undefined,
        country: formData.country || undefined,
        secondaryContactName: formData.secondaryContactName || undefined,
        secondaryContactPhone: formData.secondaryContactPhone || undefined,
        secondaryContactEmail: formData.secondaryContactEmail || undefined,
        preferredCommunicationMethod: formData.preferredCommunicationMethod || undefined,
        notes: formData.notes || undefined
      };

      let customer: Customer;
      let initialJob: Job | undefined;

      if (createInitialJob) {
        // Create customer with initial job
        const createWithJobDto: CreateCustomerWithJobDto = {
          ...cleanedFormData,
          createInitialJob: true,
          initialJobType: JobType.Consultation,
          initialJobPriority: Priority.Medium
        };
        const result = await customerService.createCustomerWithJob(createWithJobDto);
        customer = result.customer;
        initialJob = result.initialJob;
      } else {
        // Create customer only
        customer = await customerService.createCustomer(cleanedFormData);
      }

      setSuccess('Customer created');
      setError(null);

      // Call onSuccess callback with the created customer and optional job
      setTimeout(() => {
        resetForm();
        onSuccess(customer, initialJob);
      }, 500);
    } catch (err: any) {
      setError(extractApiErrorMessage(err, 'Failed to create customer'));
      setSuccess(null);
    }
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  return (
    <BaseDialog 
      open={open} 
      onClose={handleClose}
      title={title}
      fullWidth
      sx={{
        '& .MuiDialog-paper': {
          maxWidth: { xs: 'calc(100vw - 10px)', sm: '900px' }
        }
      }}
      contentSx={{ pb: 4 }}
      actions={
        <>
          <Button 
            onClick={handleClose}
            variant="outlined"
            color="primary"
          >
            Cancel
          </Button>
          <Button onClick={handleSubmit} variant="contained">Create Customer</Button>
        </>
      }
    >
      {error && (
        <Alert severity="error" sx={{ mb: 2, mt: 1, whiteSpace: 'pre-line' }}>
          {error}
        </Alert>
      )}
      {success && <Alert severity="success" sx={{ mb: 2, mt: 1 }}>{success}</Alert>}
      <Grid container spacing={2} sx={{ mt: 1 }}>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="First Name"
            value={formData.firstName}
            onChange={(e) => setFormData(prev => ({ ...prev, firstName: e.target.value }))}
            onBlur={() => handleFieldBlur('firstName')}
            error={!!validationErrors.firstName}
            helperText={validationErrors.firstName}
            required
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Last Name"
            value={formData.lastName}
            onChange={(e) => setFormData(prev => ({ ...prev, lastName: e.target.value }))}
            onBlur={() => handleFieldBlur('lastName')}
            error={!!validationErrors.lastName}
            helperText={validationErrors.lastName}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Email"
            type="email"
            value={formData.email}
            onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
            onBlur={() => handleFieldBlur('email')}
            error={!!validationErrors.email}
            helperText={validationErrors.email}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Phone Number"
            value={formData.phoneNumber}
            onChange={(e) => setFormData(prev => ({ ...prev, phoneNumber: e.target.value }))}
            onBlur={() => handleFieldBlur('phoneNumber')}
            error={!!validationErrors.phoneNumber}
            helperText={validationErrors.phoneNumber}
          />
        </Grid>
        <Grid item xs={12}>
          <GoogleMapsAutocomplete
            key={`address-create-${addressKey}`}
            value={formData.address || ''}
            onChange={handleAddressChange}
            label="Address"
            defaultBounds={{
              city: business?.defaultCity,
              country: business?.defaultCountry
            }}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            label="City"
            value={formData.city}
            onChange={(e) => setFormData(prev => ({ ...prev, city: e.target.value }))}
          />
        </Grid>
        <Grid item xs={12} md={8}>
          <FormControl fullWidth>
            <InputLabel>Source</InputLabel>
            <Select
              value={formData.source}
              onChange={(e) => setFormData(prev => ({ ...prev, source: e.target.value as CustomerSource }))}
              onBlur={() => handleFieldBlur('source')}
              label="Source"
              error={!!validationErrors.source}
            >
              {Object.values(CustomerSource).filter(v => typeof v === 'number').map((source) => (
                <MenuItem key={source} value={source}>
                  {customerService.getSourceDisplayName(source as CustomerSource)}
                </MenuItem>
              ))}
            </Select>
            {validationErrors.source && (
              <div style={{ color: '#d32f2f', fontSize: '0.75rem', marginTop: '3px', marginLeft: '14px' }}>
                {validationErrors.source}
              </div>
            )}
          </FormControl>
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Secondary Contact Name"
            value={formData.secondaryContactName}
            onChange={(e) => setFormData(prev => ({ ...prev, secondaryContactName: e.target.value }))}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Secondary Contact Phone"
            type="tel"
            value={formData.secondaryContactPhone}
            onChange={(e) => setFormData(prev => ({ ...prev, secondaryContactPhone: e.target.value }))}
          />
        </Grid>
        <Grid item xs={12} md={8}>
          <TextField
            fullWidth
            label="Secondary Contact Email"
            type="email"
            value={formData.secondaryContactEmail}
            onChange={(e) => setFormData(prev => ({ ...prev, secondaryContactEmail: e.target.value }))}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <FormControl fullWidth>
            <InputLabel>Assigned Sales Person</InputLabel>
            <Select
              value={formData.assignedSalesPersonId || ''}
              onChange={(e) => setFormData(prev => ({ ...prev, assignedSalesPersonId: e.target.value || undefined }))}
              label="Assigned Sales Person"
              disabled={loadingUsers}
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              {businessUsers.map((user) => (
                <MenuItem key={user.id} value={user.id}>
                  {user.firstName} {user.lastName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Notes"
            multiline
            rows={3}
            value={formData.notes}
            onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
          />
        </Grid>
      </Grid>
    </BaseDialog>
  );
};

export default CustomerFormDialog;
